import React, { useMemo, useState } from 'react';

const ACCENT = '#10B981';
const CYAN = '#06B6D4';
const PURPLE = '#8B5CF6';
const AMBER = '#F59E0B';
const ORANGE = '#F97316';

const CARD: React.CSSProperties = {
  background: 'var(--card)',
  border: '1px solid var(--border)',
  borderRadius: 20,
  padding: 28,
};

const FILTERS = ['All', 'Free', 'Premium', 'New', 'Popular', 'My Purchases'] as const;

const AGENTS = [
  { icon: '🧠', name: 'Advanced Sentiment AI', cat: 'Core AI', desc: 'Deep emotion analysis beyond basic sentiment', price: 'Free', primary: true },
  { icon: '🎬', name: 'Video Shorts Generator', cat: 'Content & Social', desc: 'Auto-create 60s Reels & YouTube Shorts', price: '₹999/mo', primary: false },
  { icon: '📈', name: 'Stock Market Tracker', cat: 'Revenue Multipliers', desc: 'Track stocks & alert investors automatically', price: '₹499/mo', primary: false },
  { icon: '🏘️', name: 'Property Valuation AI', cat: 'Game Changers', desc: 'Auto-calculate property values from market data', price: '₹1,499/mo', primary: false },
  { icon: '🎙️', name: 'Podcast Auto-Publisher', cat: 'Content & Social', desc: 'Record → Edit → Publish to all platforms', price: 'Free', primary: true },
  { icon: '💼', name: 'LinkedIn Outreach Pro', cat: 'Engagement & Network', desc: 'Mass personalized LinkedIn DMs', price: '₹799/mo', primary: false },
  { icon: '🌡️', name: 'Heatmap Analyzer', cat: 'Technical SEO', desc: 'Website behavior analysis & optimization', price: 'Free', primary: true },
  { icon: '🤝', name: 'Partner Finder AI', cat: 'Operations & Control', desc: 'Find & qualify business partners automatically', price: '₹299/mo', primary: false },
] as const;

const TEMPLATES = [
  { icon: '🏠', name: 'Real Estate Launch Pack', desc: 'Complete campaign for property launch: ads, posts, emails, WhatsApp', price: '₹1,999 one-time', grad: 'linear-gradient(135deg, #020B18, #051525)' },
  { icon: '🍽️', name: 'Restaurant Starter Kit', desc: 'Menu posts, festive campaigns, review templates', price: 'Free', grad: 'linear-gradient(135deg, #1A0A0A, #2A1510)' },
  { icon: '💊', name: 'Healthcare Clinic Pack', desc: 'Patient outreach, appointment reminders, health tips', price: '₹999', grad: 'linear-gradient(135deg, #0A1A1A, #0A2520)' },
  { icon: '🎓', name: 'Education Institute Pack', desc: 'Admission campaigns, parent communication, results celebration', price: 'Free', grad: 'linear-gradient(135deg, #0A0A2A, #15102A)' },
  { icon: '💍', name: 'Wedding Planner Kit', desc: 'Pre-wedding buzz, vendor coordination, photo sharing', price: '₹1,499', grad: 'linear-gradient(135deg, #2A0A1A, #250818)' },
  { icon: '🏋️', name: 'Fitness Studio Pack', desc: 'Member retention, class promos, transformation stories', price: '₹499', grad: 'linear-gradient(135deg, #0A1A0A, #102A15)' },
] as const;

const INTEGRATIONS = [
  { icon: '🔵', name: 'Razorpay', desc: 'Payment gateway', connected: true },
  { icon: '📧', name: 'Resend', desc: 'Email delivery', connected: false },
  { icon: '📱', name: 'Twilio', desc: 'SMS & Calls', connected: false },
  { icon: '🤖', name: 'ODC AI', desc: 'ODC AI Vision', connected: false },
  { icon: '🎙️', name: 'ElevenLabs', desc: 'Voice Clone', connected: false },
  { icon: '📹', name: 'HeyGen', desc: 'AI Avatars', connected: false },
  { icon: '🗓️', name: 'Google Calendar', desc: 'Scheduling', connected: false },
  { icon: '📊', name: 'Google Analytics', desc: 'Web Analytics', connected: false },
] as const;

const CAT_COLORS: Record<string, string> = {
  'Core AI': ACCENT,
  'Content & Social': CYAN,
  'Revenue Multipliers': AMBER,
  'Game Changers': PURPLE,
  'Engagement & Network': '#EC4899',
  'Technical SEO': ORANGE,
  'Operations & Control': '#6B7280',
};

export function MarketplacePage() {
  const [activeTab, setActiveTab] = useState<'agents' | 'templates' | 'integrations'>('agents');
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>('All');

  const filteredAgents = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return AGENTS.filter((a) => {
      if (q && !(`${a.name} ${a.cat} ${a.desc}`.toLowerCase().includes(q))) return false;
      if (filter === 'Free' && a.price !== 'Free') return false;
      if (filter === 'Premium' && a.price === 'Free') return false;
      if (filter === 'My Purchases') return false;
      return true;
    });
  }, [searchQuery, filter]);

  const filteredTemplates = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return TEMPLATES.filter((t) => !q || `${t.name} ${t.desc}`.toLowerCase().includes(q));
  }, [searchQuery]);

  const filteredInt = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return INTEGRATIONS.filter((i) => !q || `${i.name} ${i.desc}`.toLowerCase().includes(q));
  }, [searchQuery]);

  const pill = (on: boolean): React.CSSProperties => ({
    border: `1px solid ${on ? PURPLE : 'var(--border)'}`,
    borderRadius: 999,
    padding: '6px 14px',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    background: on ? 'rgba(139,92,246,0.12)' : 'var(--card2)',
    color: on ? PURPLE : 'var(--muted)',
  });

  return (
    <div className="odc-page relative z-10" style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
      <style>{`
        .odc-mp-agents { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
        @media (max-width: 1000px) { .odc-mp-agents { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 600px) { .odc-mp-agents { grid-template-columns: 1fr !important; } }
        .odc-mp-tpl { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
        @media (max-width: 900px) { .odc-mp-tpl { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 560px) { .odc-mp-tpl { grid-template-columns: 1fr !important; } }
        .odc-mp-int { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
        @media (max-width: 1000px) { .odc-mp-int { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 520px) { .odc-mp-int { grid-template-columns: 1fr !important; } }
      `}</style>

      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 20, alignItems: 'flex-start' }}>
        <div style={{ flex: '1 1 280px', minWidth: 0 }}>
          <span
            style={{
              display: 'inline-flex',
              padding: '5px 14px',
              borderRadius: 999,
              border: '1px solid rgba(139,92,246,0.3)',
              background: 'rgba(139,92,246,0.08)',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.14em',
              color: PURPLE,
              textTransform: 'uppercase',
            }}
          >
            ODC MARKETPLACE
          </span>
          <h1 style={{ margin: '12px 0 0', fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)', fontWeight: 800, color: 'var(--text)' }}>
            ODC Marketplace
          </h1>
          <p style={{ margin: '12px 0 0', color: 'var(--muted)', fontSize: 15, maxWidth: 560, lineHeight: 1.6 }}>
            Discover premium AI agents, templates &amp; integrations. Built by ODC &amp; community developers.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {['🤖 AI Agents', '📦 50+ Templates', '🔌 20+ Integrations'].map((t) => (
            <span
              key={t}
              style={{
                border: '1px solid var(--border)',
                borderRadius: 999,
                padding: '8px 14px',
                background: 'var(--card)',
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--text)',
              }}
            >
              {t}
            </span>
          ))}
        </div>
      </div>

      <div className="odc-card" style={{ ...CARD, transform: 'none' }}>
        <input
          className="odc-input"
          style={{ width: '100%', fontSize: 16, padding: '14px 16px' }}
          placeholder="Search agents, templates, integrations..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14 }}>
          {FILTERS.map((f) => (
            <button key={f} type="button" onClick={() => setFilter(f)} style={pill(filter === f)}>
              {f}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
        {(
          [
            { id: 'agents' as const, label: '🤖 AI Agents' },
            { id: 'templates' as const, label: '📦 Templates' },
            { id: 'integrations' as const, label: '🔌 Integrations' },
          ] as const
        ).map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setActiveTab(t.id)}
            style={{
              padding: '12px 24px',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 600,
              border: 'none',
              background: 'none',
              borderBottom: activeTab === t.id ? `2px solid ${PURPLE}` : '2px solid transparent',
              color: activeTab === t.id ? PURPLE : 'var(--muted)',
              marginBottom: -1,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'agents' ? (
        <>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>Featured Agents</h2>
          <div className="odc-mp-agents">
            {filteredAgents.map((a) => (
              <div key={a.name} className="odc-card" style={{ ...CARD, padding: 24, position: 'relative', transform: 'none' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      background: `${CAT_COLORS[a.cat] || ACCENT}22`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 22,
                    }}
                  >
                    {a.icon}
                  </div>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 800,
                      padding: '4px 10px',
                      borderRadius: 8,
                      background: 'var(--card2)',
                      border: '1px solid var(--border)',
                      color: 'var(--text)',
                    }}
                  >
                    {a.price}
                  </span>
                </div>
                <h3 style={{ margin: '12px 0 0', fontWeight: 700, fontSize: 16, color: 'var(--text)' }}>{a.name}</h3>
                <span
                  style={{
                    display: 'inline-block',
                    marginTop: 8,
                    fontSize: 10,
                    fontWeight: 700,
                    padding: '3px 8px',
                    borderRadius: 6,
                    background: `${CAT_COLORS[a.cat] || ACCENT}22`,
                    color: CAT_COLORS[a.cat] || ACCENT,
                  }}
                >
                  {a.cat}
                </span>
                <p style={{ margin: '10px 0 0', fontSize: 13, color: 'var(--muted)', lineHeight: 1.5, minHeight: 40 }}>{a.desc}</p>
                <p style={{ margin: '12px 0 0', fontSize: 11, color: 'var(--muted)' }}>
                  ⭐ 4.8 · 💾 1.2k installs · 🔄 Updated 2d ago
                </p>
                <button
                  type="button"
                  className={a.primary ? 'odc-btn-primary' : 'odc-btn-ghost'}
                  style={{ width: '100%', marginTop: 14, justifyContent: 'center' }}
                >
                  {a.primary ? 'Install' : 'Buy'}
                </button>
              </div>
            ))}
          </div>
        </>
      ) : null}

      {activeTab === 'templates' ? (
        <div className="odc-mp-tpl">
          {filteredTemplates.map((t) => (
            <div key={t.name} className="odc-card" style={{ ...CARD, padding: 0, overflow: 'hidden', transform: 'none' }}>
              <div style={{ height: 120, background: t.grad, padding: 16, color: '#fff', fontSize: 14, fontWeight: 700 }}>
                {t.icon} {t.name.split(' ').slice(0, 3).join(' ')}
              </div>
              <div style={{ padding: 20 }}>
                <h3 style={{ margin: 0, fontWeight: 700, fontSize: 16, color: 'var(--text)' }}>{t.name}</h3>
                <p style={{ margin: '10px 0 0', fontSize: 13, color: 'var(--muted)', lineHeight: 1.5 }}>{t.desc}</p>
                <p style={{ margin: '12px 0 0', fontWeight: 700, color: ACCENT, fontSize: 14 }}>{t.price}</p>
                <button type="button" className="odc-btn-primary" style={{ width: '100%', marginTop: 14, justifyContent: 'center' }}>
                  Get Template
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {activeTab === 'integrations' ? (
        <>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>Connect Your Tools</h2>
          <div className="odc-mp-int">
            {filteredInt.map((i) => (
              <div key={i.name} className="odc-card" style={{ ...CARD, padding: 20, transform: 'none' }}>
                <div style={{ fontSize: 28 }}>{i.icon}</div>
                <h3 style={{ margin: '10px 0 0', fontWeight: 700, fontSize: 15, color: 'var(--text)' }}>{i.name}</h3>
                <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--muted)' }}>{i.desc}</p>
                {i.connected ? (
                  <>
                    <span className="odc-badge odc-badge-green" style={{ marginTop: 12, display: 'inline-block', fontSize: 10 }}>
                      Connected
                    </span>
                    <button type="button" className="odc-btn-ghost" style={{ width: '100%', marginTop: 12, justifyContent: 'center', fontSize: 12 }}>
                      Manage
                    </button>
                  </>
                ) : (
                  <button type="button" className="odc-btn-primary" style={{ width: '100%', marginTop: 14, justifyContent: 'center', fontSize: 12 }}>
                    Connect
                  </button>
                )}
              </div>
            ))}
          </div>
        </>
      ) : null}

      <div
        className="odc-card"
        style={{
          ...CARD,
          transform: 'none',
          border: '2px solid transparent',
          background: 'linear-gradient(var(--card), var(--card)) padding-box, linear-gradient(135deg, rgba(139,92,246,0.5), rgba(139,92,246,0.15)) border-box',
          textAlign: 'center',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: 'var(--text)' }}>Build &amp; Sell on ODC Marketplace</h2>
        <p style={{ margin: '10px 0 0', color: 'var(--muted)', fontSize: 14 }}>
          Developers earn 70% revenue share on every sale
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 16, marginTop: 20 }}>
          {['💰 70% Revenue Share', '🌍 10,000+ ODC Users', '🛠️ Free Dev Tools'].map((x) => (
            <div
              key={x}
              style={{
                border: '1px solid var(--border)',
                borderRadius: 14,
                padding: '14px 20px',
                background: 'var(--bg2)',
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--text)',
              }}
            >
              {x}
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flexWrap: 'wrap', marginTop: 24, fontSize: 13, color: 'var(--muted)' }}>
          <span>1. Apply</span>
          <span>→</span>
          <span>2. Build</span>
          <span>→</span>
          <span>3. Submit</span>
          <span>→</span>
          <span>4. Earn</span>
        </div>
        <button type="button" className="odc-btn-primary" style={{ marginTop: 20 }} disabled title="Coming Q4 2026">
          Apply as Developer
        </button>
      </div>
    </div>
  );
}
